/* Vřídlo motion layer. Progressive enhancement only: every page works without this file.
   Features (all opt-in through classes or data attributes):
     .vr-reveal / .vr-lines--reveal   scroll reveal (adds .is-in once, one shot)
     [data-vr-stagger]                numbers direct .vr-reveal children into --vr-i
     [data-vr-stage]                  pointer parallax: sets --vr-mx / --vr-my (-1..1, eased) on the element
     [data-vr-tilt="deg"]             3D tilt + glare: --vr-rx --vr-ry --vr-gx --vr-gy, class .is-tracking
     [data-vr-scroll]                 scroll progress while visible: --vr-p (0..1) and --vr-py (px from centre)
     [data-vr-count="n"]              number counter; the final text stays in the markup
     .vr-scroller                     snap carousel: [data-vr-track] [data-vr-prev] [data-vr-next] [data-vr-dots]
     .vr-marquee                      pauses its animation while off screen
   It respects prefers-reduced-motion, needs a fine pointer for pointer effects, and re-initialises
   sections in the theme editor (shopify:section:load / unload). */
(function () {
  if (window.vrMotion) return;

  var root = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  var hasIO = 'IntersectionObserver' in window;
  var registry = new Map();
  var motionUpdates = new Set();

  function paused() {
    return reduced.matches || document.hidden;
  }

  function select(scope, selector) {
    var nodes = Array.prototype.slice.call(scope.querySelectorAll(selector));
    if (scope.matches && scope.matches(selector)) nodes.unshift(scope);
    return nodes;
  }

  function own(el, cleanup) {
    var list = registry.get(el) || [];
    list.push(cleanup);
    registry.set(el, list);
  }

  function release(scope) {
    if (tiltActive && (tiltActive === scope || scope.contains(tiltActive))) resetPointerEffects();
    registry.forEach(function (list, el) {
      if (el === scope || (scope.contains && scope.contains(el))) {
        list.forEach(function (fn) { fn(); });
        registry.delete(el);
      }
    });
  }

  function watchMotion(el, update) {
    motionUpdates.add(update);
    own(el, function () { motionUpdates.delete(update); });
    update();
  }

  function designMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  /* ---------- scroll reveal ---------- */
  var revealObserver = null;
  var revealPending = new Set();

  function revealNow(el) {
    el.classList.add('is-in');
    el.removeAttribute('data-vr-pending');
    revealPending.delete(el);
    if (revealObserver) revealObserver.unobserve(el);
  }

  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // A scroll jump can skip the entire element between observer callbacks.
        if (!entry.isIntersecting && entry.boundingClientRect.bottom > 0) return;
        revealNow(entry.target);
      });
    }, { threshold: 0 });
    return revealObserver;
  }

  function initReveal(scope) {
    select(scope, '[data-vr-stagger]').forEach(function (group) {
      Array.prototype.forEach.call(group.querySelectorAll('.vr-reveal'), function (child, index) {
        if (!child.style.getPropertyValue('--vr-i')) child.style.setProperty('--vr-i', String(index));
      });
    });
    var nodes = select(scope, '.vr-reveal, .vr-lines--reveal');
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.classList.contains('is-in') || el.hasAttribute('data-vr-seen')) return;
      el.setAttribute('data-vr-seen', '');
      // Content in the initial viewport stays readable from its first paint.
      if (!hasIO || reduced.matches || designMode() || el.getBoundingClientRect().top < window.innerHeight) {
        revealNow(el);
        return;
      }
      el.setAttribute('data-vr-pending', '');
      revealPending.add(el);
      getRevealObserver().observe(el);
      own(el, function () {
        if (revealObserver) revealObserver.unobserve(el);
        revealPending.delete(el);
        el.removeAttribute('data-vr-pending');
        el.removeAttribute('data-vr-seen');
      });
    });
  }

  /* ---------- pointer parallax stage ---------- */
  function initStage(el) {
    if (el.hasAttribute('data-vr-bound')) return;
    el.setAttribute('data-vr-bound', '');
    var tx = 0;
    var ty = 0;
    var cx = 0;
    var cy = 0;
    var raf = 0;
    var visible = el.getBoundingClientRect().bottom > 0 && el.getBoundingClientRect().top < window.innerHeight;
    var observer = null;

    function reset() {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      tx = ty = cx = cy = 0;
      el.style.removeProperty('--vr-mx');
      el.style.removeProperty('--vr-my');
    }

    function update() {
      if (paused() || !visible || !finePointer.matches) reset();
    }

    function loop() {
      raf = 0;
      if (paused() || !visible || !finePointer.matches) { reset(); return; }
      cx += (tx - cx) * 0.085;
      cy += (ty - cy) * 0.085;
      el.style.setProperty('--vr-mx', cx.toFixed(4));
      el.style.setProperty('--vr-my', cy.toFixed(4));
      raf = Math.abs(tx - cx) > 0.001 || Math.abs(ty - cy) > 0.001 ? requestAnimationFrame(loop) : 0;
    }

    function wake() {
      if (!raf) raf = requestAnimationFrame(loop);
    }

    function move(event) {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      if (!finePointer.matches || paused() || !visible) return;
      var box = el.getBoundingClientRect();
      if (!box.width || !box.height) return;
      tx = clamp(((event.clientX - box.left) / box.width - 0.5) * 2, -1, 1);
      ty = clamp(((event.clientY - box.top) / box.height - 0.5) * 2, -1, 1);
      wake();
    }

    function leave() {
      if (paused() || !visible || !finePointer.matches) { reset(); return; }
      tx = 0;
      ty = 0;
      wake();
    }

    el.addEventListener('pointermove', move, { passive: true });
    el.addEventListener('pointerleave', leave);
    if (hasIO) {
      observer = new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        update();
      });
      observer.observe(el);
    }
    watchMotion(el, update);
    own(el, function () {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerleave', leave);
      if (observer) observer.disconnect();
      reset();
      el.removeAttribute('data-vr-bound');
    });
  }

  /* ---------- delegated tilt (installed once) ---------- */
  var tiltActive = null;
  var tiltFrame = 0;
  var tiltPoint = null;

  function tiltReset(el) {
    el.classList.remove('is-tracking');
    ['--vr-rx', '--vr-ry', '--vr-gx', '--vr-gy'].forEach(function (name) { el.style.removeProperty(name); });
  }

  function tiltPaint() {
    tiltFrame = 0;
    if (!tiltActive || !tiltPoint || paused() || !finePointer.matches || !tiltActive.isConnected) return;
    var box = tiltActive.getBoundingClientRect();
    if (!box.width || !box.height) return;
    var x = clamp((tiltPoint.x - box.left) / box.width, 0, 1);
    var y = clamp((tiltPoint.y - box.top) / box.height, 0, 1);
    var max = parseFloat(tiltActive.getAttribute('data-vr-tilt')) || 6;
    tiltActive.classList.add('is-tracking');
    tiltActive.style.setProperty('--vr-rx', ((0.5 - y) * max).toFixed(2) + 'deg');
    tiltActive.style.setProperty('--vr-ry', ((x - 0.5) * max).toFixed(2) + 'deg');
    tiltActive.style.setProperty('--vr-gx', (x * 100).toFixed(1) + '%');
    tiltActive.style.setProperty('--vr-gy', (y * 100).toFixed(1) + '%');
  }

  function resetPointerEffects() {
    if (tiltFrame) cancelAnimationFrame(tiltFrame);
    tiltFrame = 0;
    if (tiltActive) tiltReset(tiltActive);
    tiltActive = tiltPoint = null;
  }

  function installPointerEffects() {
    document.addEventListener('pointermove', function (event) {
      if (event.pointerType !== 'mouse' || !finePointer.matches || paused()) return;
      var target = event.target && event.target.closest ? event.target : null;

      var tilt = target ? target.closest('[data-vr-tilt]') : null;
      if (tiltActive && tiltActive !== tilt) tiltReset(tiltActive);
      tiltActive = tilt;
      if (tilt) {
        tiltPoint = { x: event.clientX, y: event.clientY };
        if (!tiltFrame) tiltFrame = requestAnimationFrame(tiltPaint);
      }
    }, { passive: true });

    document.addEventListener('pointerleave', resetPointerEffects, true);
  }

  /* ---------- scroll progress ---------- */
  var scrollNodes = new Set();
  var scrollFrame = 0;
  var scrollObserver = null;
  var scrollBound = false;
  var scrollTargets = new Set();

  function scrollPaint() {
    scrollFrame = 0;
    if (paused()) return;
    var vh = window.innerHeight || 1;
    scrollNodes.forEach(function (el) {
      var box = el.getBoundingClientRect();
      var p = clamp((vh - box.top) / (vh + box.height), 0, 1);
      el.style.setProperty('--vr-p', p.toFixed(4));
      el.style.setProperty('--vr-py', (box.top + box.height / 2 - vh / 2).toFixed(1));
    });
  }

  function scrollWake() {
    if (!paused() && !scrollFrame && scrollNodes.size) scrollFrame = requestAnimationFrame(scrollPaint);
  }

  function initScrollProgress(el) {
    if (!hasIO || el.hasAttribute('data-vr-scroll-bound')) return;
    el.setAttribute('data-vr-scroll-bound', '');
    scrollTargets.add(el);
    if (!scrollObserver) {
      scrollObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!scrollTargets.has(entry.target)) return;
          if (entry.isIntersecting) scrollNodes.add(entry.target);
          else scrollNodes.delete(entry.target);
        });
        scrollWake();
      }, { rootMargin: '10% 0px' });
    }
    if (!scrollBound) {
      scrollBound = true;
      window.addEventListener('scroll', scrollWake, { passive: true });
      window.addEventListener('resize', scrollWake, { passive: true });
    }
    scrollObserver.observe(el);
    watchMotion(el, function () {
      if (paused()) {
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
        scrollFrame = 0;
        el.style.removeProperty('--vr-p');
        el.style.removeProperty('--vr-py');
      } else scrollWake();
    });
    own(el, function () {
      scrollObserver.unobserve(el);
      scrollNodes.delete(el);
      scrollTargets.delete(el);
      el.style.removeProperty('--vr-p');
      el.style.removeProperty('--vr-py');
      if (!scrollNodes.size && scrollFrame) { cancelAnimationFrame(scrollFrame); scrollFrame = 0; }
      if (!scrollTargets.size) {
        window.removeEventListener('scroll', scrollWake);
        window.removeEventListener('resize', scrollWake);
        scrollBound = false;
      }
      el.removeAttribute('data-vr-scroll-bound');
    });
  }

  /* ---------- counters ---------- */
  var countObserver = null;
  var counters = new Map();

  function runCount(state) {
    var duration = 1500;
    state.started = true;
    function frame(now) {
      state.raf = 0;
      if (paused() || !state.visible || !state.el.isConnected) return;
      if (state.last !== null) state.elapsed += now - state.last;
      state.last = now;
      var t = clamp(state.elapsed / duration, 0, 1);
      var eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
      if (t < 1) {
        state.el.textContent = state.format.format(state.target * eased);
        state.raf = requestAnimationFrame(frame);
      } else {
        state.complete = true;
        state.el.textContent = state.original;
        countObserver.unobserve(state.el);
      }
    }
    state.last = null;
    state.raf = requestAnimationFrame(frame);
  }

  function initCount(el) {
    if (!hasIO || designMode() || el.hasAttribute('data-vr-count-bound')) return;
    var target = parseFloat(el.getAttribute('data-vr-count'));
    if (!isFinite(target)) return;
    var decimals = clamp(parseInt(el.getAttribute('data-vr-decimals') || '0', 10) || 0, 0, 20);
    var format;
    try {
      format = new Intl.NumberFormat(root.lang || undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    } catch (error) { return; }
    var state = { el: el, target: target, format: format, original: el.textContent, raf: 0, elapsed: 0, last: null, visible: false, started: false, complete: reduced.matches };
    counters.set(el, state);
    el.setAttribute('data-vr-count-bound', '');
    function update() {
      if (paused() || !state.visible) {
        if (state.raf) cancelAnimationFrame(state.raf);
        state.raf = 0;
        state.last = null;
        // Never leave a factual amount at an intermediate animated value.
        if (state.started || reduced.matches) {
          state.complete = true;
          el.textContent = state.original;
          if (countObserver) countObserver.unobserve(el);
        }
      } else if (!state.complete && !state.raf) runCount(state);
    }
    if (!countObserver) {
      countObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var current = counters.get(entry.target);
          if (!current) return;
          current.visible = entry.isIntersecting;
          current.update();
        });
      }, { threshold: 0.6 });
    }
    state.update = update;
    if (!state.complete) countObserver.observe(el);
    watchMotion(el, update);
    own(el, function () {
      if (state.raf) cancelAnimationFrame(state.raf);
      state.raf = 0;
      countObserver.unobserve(el);
      counters.delete(el);
      el.textContent = state.original;
      el.removeAttribute('data-vr-count-bound');
    });
  }

  /* ---------- marquee ---------- */
  function initMarquee(el) {
    if (!hasIO || el.hasAttribute('data-vr-marquee-bound')) return;
    el.setAttribute('data-vr-marquee-bound', '');
    var visible = false;
    function update() { el.classList.toggle('is-paused', paused() || !visible); }
    var observer = new IntersectionObserver(function (entries) {
      visible = entries[0].isIntersecting;
      update();
    });
    observer.observe(el);
    watchMotion(el, update);
    own(el, function () {
      observer.disconnect();
      el.classList.remove('is-paused');
      el.removeAttribute('data-vr-marquee-bound');
    });
  }

  /* ---------- snap scroller ---------- */
  function initScroller(el) {
    if (el.hasAttribute('data-vr-scroller-bound')) return;
    var track = el.querySelector('[data-vr-track]');
    if (!track) return;
    el.setAttribute('data-vr-scroller-bound', '');

    var prev = el.querySelector('[data-vr-prev]');
    var next = el.querySelector('[data-vr-next]');
    var dotsWrap = el.querySelector('[data-vr-dots]');
    var dots = [];
    var abort = new AbortController();
    var options = { signal: abort.signal };
    var frame = 0;
    var dragTimer = 0;

    function items() {
      return Array.prototype.slice.call(track.children);
    }

    function behavior() {
      return reduced.matches ? 'auto' : 'smooth';
    }

    function pad() {
      return parseFloat(getComputedStyle(track).paddingLeft) || 0;
    }

    function goTo(index) {
      var item = items()[index];
      if (!item) return;
      var left = item.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft - pad();
      track.scrollTo({ left: left, behavior: behavior() });
    }

    function current() {
      var origin = track.getBoundingClientRect().left + pad();
      var best = 0;
      var bestDistance = Infinity;
      items().forEach(function (item, index) {
        var distance = Math.abs(item.getBoundingClientRect().left - origin);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });
      return best;
    }

    function sync() {
      frame = 0;
      var max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
      var index = current();
      if (track.scrollLeft >= max && max > 0) index = items().length - 1;
      dots.forEach(function (dot, i) {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    function queueSync() {
      if (!frame) frame = requestAnimationFrame(sync);
    }

    function step(direction) {
      var index = current();
      var max = track.scrollWidth - track.clientWidth - 2;
      var target = clamp(index + direction, 0, items().length - 1);
      if (direction > 0 && track.scrollLeft >= max) return;
      goTo(target);
    }

    if (dotsWrap) {
      var template = dotsWrap.getAttribute('data-label-template') || '%d';
      dotsWrap.textContent = '';
      items().forEach(function (item, index) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'vr-scroller__dot';
        dot.setAttribute('aria-label', template.replace('%d', String(index + 1)));
        dot.addEventListener('click', function () { goTo(index); }, options);
        dotsWrap.appendChild(dot);
        dots.push(dot);
      });
    }

    if (prev) prev.addEventListener('click', function () { step(-1); }, options);
    if (next) next.addEventListener('click', function () { step(1); }, options);
    track.addEventListener('scroll', queueSync, { passive: true, signal: abort.signal });
    window.addEventListener('resize', queueSync, { passive: true, signal: abort.signal });

    track.addEventListener('keydown', function (event) {
      if (event.target !== track) return;
      if (event.key === 'ArrowRight') { event.preventDefault(); step(1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); step(-1); }
    }, options);

    /* Mouse drag. Touch and pen use the native scrolling. */
    var drag = null;
    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = { x: event.clientX, left: track.scrollLeft, moved: false };
    }, options);
    window.addEventListener('pointermove', function (event) {
      if (!drag) return;
      var dx = event.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) > 6) {
        drag.moved = true;
        track.classList.add('is-dragging');
        track.style.scrollSnapType = 'none';
      }
      if (drag.moved) track.scrollLeft = drag.left - dx;
    }, { passive: true, signal: abort.signal });
    function endDrag(cancelled) {
      if (!drag) return;
      var moved = drag.moved;
      drag = null;
      if (moved) {
        track.classList.remove('is-dragging');
        track.style.scrollSnapType = '';
        if (cancelled) return;
        goTo(current());
        if (dragTimer) window.clearTimeout(dragTimer);
        dragTimer = window.setTimeout(function () { dragTimer = 0; track.removeAttribute('data-vr-dragged'); }, 0);
        track.setAttribute('data-vr-dragged', '');
      }
    }
    window.addEventListener('pointerup', function () { endDrag(false); }, options);
    window.addEventListener('pointercancel', function () { endDrag(true); }, options);
    track.addEventListener('click', function (event) {
      if (track.hasAttribute('data-vr-dragged')) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, { capture: true, signal: abort.signal });

    sync();
    own(el, function () {
      abort.abort();
      if (frame) cancelAnimationFrame(frame);
      if (dragTimer) window.clearTimeout(dragTimer);
      drag = null;
      track.classList.remove('is-dragging');
      track.style.scrollSnapType = '';
      track.removeAttribute('data-vr-dragged');
      el.removeAttribute('data-vr-scroller-bound');
    });
  }

  /* ---------- header scrolled flag (used by the glass header) ---------- */
  var scrolledBound = false;

  function initScrolledFlag() {
    if (scrolledBound) return;
    scrolledBound = true;
    var ticking = false;

    function update() {
      ticking = false;
      root.classList.toggle('vr-scrolled', (window.pageYOffset || 0) > 24);
      // IO need not emit an entry when a scroll jump moves a node from below to
      // above the viewport without an intersecting frame in between.
      revealPending.forEach(function (el) {
        if (el.getBoundingClientRect().top < window.innerHeight) revealNow(el);
      });
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }, { passive: true });
    update();
  }

  /* ---------- init ---------- */
  function init(scope) {
    scope = scope || document;
    initReveal(scope);
    select(scope, '[data-vr-stage]').forEach(initStage);
    select(scope, '[data-vr-scroll]').forEach(initScrollProgress);
    select(scope, '[data-vr-count]').forEach(initCount);
    select(scope, '.vr-marquee').forEach(initMarquee);
    select(scope, '.vr-scroller').forEach(initScroller);
  }

  function refreshMotion() {
    root.classList.toggle('vr-motion-paused', paused());
    if (paused() || !finePointer.matches) resetPointerEffects();
    if (reduced.matches) document.querySelectorAll('[data-vr-pending]').forEach(revealNow);
    motionUpdates.forEach(function (update) { update(); });
  }

  reduced.addEventListener('change', refreshMotion);
  finePointer.addEventListener('change', refreshMotion);
  document.addEventListener('visibilitychange', refreshMotion);
  document.addEventListener('focusin', function (event) {
    var el = event.target;
    while (el && el !== document) {
      if (el.hasAttribute('data-vr-pending')) revealNow(el);
      el = el.parentElement;
    }
  });

  window.vrMotion = { init: init, release: release };

  installPointerEffects();
  initScrolledFlag();
  init(document);
  refreshMotion();
  root.classList.add('vr-ready');

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { release(event.target); });
})();
